import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import FormatAlignCenterOutlined from "@mui/icons-material/FormatAlignCenterOutlined";
import FormatAlignJustifyOutlined from "@mui/icons-material/FormatAlignJustifyOutlined";
import FormatAlignLeftOutlined from "@mui/icons-material/FormatAlignLeftOutlined";
import FormatAlignRightOutlined from "@mui/icons-material/FormatAlignRightOutlined";
import FormatColorFillOutlined from "@mui/icons-material/FormatColorFillOutlined";
import FormatBoldOutlined from "@mui/icons-material/FormatBoldOutlined";
import FormatColorTextOutlined from "@mui/icons-material/FormatColorTextOutlined";
import FormatItalicOutlined from "@mui/icons-material/FormatItalicOutlined";
import FormatIndentDecreaseOutlined from "@mui/icons-material/FormatIndentDecreaseOutlined";
import FormatIndentIncreaseOutlined from "@mui/icons-material/FormatIndentIncreaseOutlined";
import FormatListBulletedOutlined from "@mui/icons-material/FormatListBulletedOutlined";
import FormatListNumberedOutlined from "@mui/icons-material/FormatListNumberedOutlined";
import FormatQuoteOutlined from "@mui/icons-material/FormatQuoteOutlined";
import FormatUnderlinedOutlined from "@mui/icons-material/FormatUnderlinedOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import StrikethroughSOutlined from "@mui/icons-material/StrikethroughSOutlined";
import { WorkspaceUserAvatar } from "./WorkspaceUserHoverCard";

const workspaceUrlRegexSource = String.raw`(?:https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)`;
const workspaceColorPatternSource = String.raw`#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?`;
const trailingUrlPunctuationPattern = /[)\].,!?:;]$/;
const workspaceEditorTextColors = ["#111827", "#4b5563", "#0f766e", "#2563eb", "#7c3aed", "#b45309", "#dc2626"];
const workspaceEditorBackgroundColors = ["#fef3c7", "#dcfce7", "#dbeafe", "#ede9fe", "#fee2e2", "#f3f4f6", "#ffffff"];

const normalizeMentionHandle = (value = "") => {
  const clean = `${value || ""}`.trim().replace(/^@+/, "");
  return clean ? `@${clean}` : "";
};

const normalizeMentionOption = (option = {}) => {
  const profile = option.profile || option.user || option;
  const label = option.label || option.name || profile.name || profile.email || "Workspace user";
  const handle = normalizeMentionHandle(option.handle || profile.username || profile.handle || `${profile.email || ""}`.split("@")[0] || label);
  return {
    handle,
    label,
    profile,
    subtitle: option.subtitle || (profile.email && profile.email !== label ? profile.email : ""),
  };
};

const escapeWorkspaceMarkdownLinkLabel = (text = "") => `${text || ""}`.replace(/\\/g, "\\\\").replace(/\]/g, "\\]");

const getSafeWorkspaceTextColor = (value = "") => {
  const color = `${value || ""}`.trim();
  return new RegExp(`^${workspaceColorPatternSource}$`, "i").test(color) ? color : workspaceEditorTextColors[0];
};

export const trimWorkspaceUrlToken = (value = "") => {
  let url = `${value || ""}`;
  let suffix = "";

  while (trailingUrlPunctuationPattern.test(url)) {
    suffix = `${url.slice(-1)}${suffix}`;
    url = url.slice(0, -1);
  }

  return { url, suffix };
};

export const getSafeWorkspaceUrl = (value = "") => {
  const { url, suffix } = trimWorkspaceUrlToken(value);
  const href = /^www\./i.test(url) ? `https://${url}` : url;

  try {
    const parsed = new URL(href);
    if (!["http:", "https:"].includes(parsed.protocol)) return null;
    return { href: parsed.href, text: url, suffix };
  } catch {
    return null;
  }
};

const escapeWorkspaceHtml = (value = "") => `${value || ""}`
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const escapeWorkspaceMarkdownInline = (value = "") => `${value || ""}`.replace(/\n+/g, " ").trim();

const getWorkspaceEditorCodeBlockHtml = (code = "", { allowAttachments = false } = {}) => `<pre><span class="workspace-editor-code-actions" contenteditable="false"><button aria-label="Copy code" data-workspace-code-action="copy" title="Copy code" type="button">⧉</button>${allowAttachments ? `<button aria-label="Attach image or file" data-workspace-code-action="attachment" title="Attach image or file" type="button"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="m20.5 11.5-8.65 8.65a5 5 0 0 1-7.07-7.07l9.2-9.2a3.5 3.5 0 1 1 4.95 4.95l-9.2 9.2a2 2 0 0 1-2.83-2.83l8.15-8.15" /></svg></button>` : ""}<button aria-label="Delete code block" data-workspace-code-action="delete" title="Delete code block" type="button"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M10 11v6m4-6v6M9 7l1-2h4l1 2m-9 0 1 13h10l1-13" /></svg></button><button aria-label="Continue as normal text" data-workspace-code-action="continue" title="Continue as normal text (Ctrl/Cmd + Enter)" type="button">↵</button></span><code>${escapeWorkspaceHtml(code)}</code></pre>`;

const getWorkspaceMarkdownListEntry = (line = "") => {
  const unordered = `${line}`.match(/^(\s*)[-*+]\s+(.+)$/);
  const ordered = `${line}`.match(/^(\s*)\d+[.)]\s+(.+)$/);
  const match = unordered || ordered;
  if (!match) return null;
  return {
    content: match[2] || "",
    depth: Math.floor(match[1].replace(/\t/g, "  ").length / 2),
    type: unordered ? "unordered" : "ordered",
  };
};

const renderWorkspaceMarkdownListHtml = (entries = []) => {
  let cursor = 0;
  const renderList = (depth, type) => {
    const tag = type === "ordered" ? "ol" : "ul";
    let html = `<${tag}>`;
    while (cursor < entries.length) {
      const entry = entries[cursor];
      if (entry.depth < depth || (entry.depth === depth && entry.type !== type)) break;
      if (entry.depth > depth) {
        const previous = entries[cursor - 1];
        if (previous) html += renderList(entry.depth, entry.type);
        continue;
      }
      cursor += 1;
      let children = "";
      while (cursor < entries.length && entries[cursor].depth > depth) {
        children += renderList(entries[cursor].depth, entries[cursor].type);
      }
      html += `<li>${renderWorkspaceInlineHtml(entry.content)}${children}</li>`;
    }
    return `${html}</${tag}>`;
  };
  let html = "";
  while (cursor < entries.length) html += renderList(entries[cursor].depth, entries[cursor].type);
  return html;
};

const getWorkspaceStyleColor = (value = "") => {
  const color = `${value || ""}`.trim();
  if (!color) return "";
  if (/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(color)) return color;
  const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!rgbMatch) return "";
  return `#${rgbMatch.slice(1, 4).map((part) => Math.max(0, Math.min(255, Number.parseInt(part, 10) || 0)).toString(16).padStart(2, "0")).join("")}`;
};

const getWorkspaceTextAlignment = (element) => {
  const alignment = `${element?.style?.textAlign || ""}`.toLowerCase();
  return ["center", "right", "justify"].includes(alignment) ? alignment : "";
};

const withWorkspaceTextAlignment = (value = "", element) => {
  const alignment = getWorkspaceTextAlignment(element);
  return alignment ? `<div style="text-align:${alignment}">${value}</div>` : value;
};

const renderWorkspaceInlineHtml = (body = "") => {
  const text = `${body || ""}`;
  if (!text) return "";
  const inlinePattern = new RegExp(String.raw`!\[([^\]\n]*)\]\((${workspaceUrlRegexSource})\)|<span\s+style=["']background-color:\s*(${workspaceColorPatternSource})["']>([\s\S]*?)<\/span>|<span\s+style=["']color:\s*(${workspaceColorPatternSource})["']>([\s\S]*?)<\/span>|<u>([\s\S]*?)<\/u>|~~([^~\n]+)~~|\[([^\]\n]+)\]\((${workspaceUrlRegexSource})\)|\*\*([^*\n]+)\*\*|` + "`([^`\\n]+)`" + String.raw`|\*([^*\n]+)\*|(${workspaceUrlRegexSource})`, "gi");
  let html = "";
  let lastIndex = 0;
  let match = inlinePattern.exec(text);

  while (match) {
    const matchIndex = match.index;
    html += escapeWorkspaceHtml(text.slice(lastIndex, matchIndex));
    const [rawToken, markdownImageAlt, markdownImageUrl, backgroundColor, backgroundText, textColor, coloredText, underlineText, strikeText, markdownLinkText, markdownLinkUrl, boldText, codeText, italicText, rawUrl] = match;
    const safeMarkdownImageUrl = markdownImageUrl ? getSafeWorkspaceUrl(markdownImageUrl) : null;
    const safeMarkdownUrl = markdownLinkUrl ? getSafeWorkspaceUrl(markdownLinkUrl) : null;
    const safeRawUrl = rawUrl ? getSafeWorkspaceUrl(rawUrl) : null;

    if (markdownImageUrl && safeMarkdownImageUrl) {
      html += `<a class="workspace-rich-image-link" href="${escapeWorkspaceHtml(safeMarkdownImageUrl.href)}" rel="noreferrer noopener" target="_blank"><img alt="${escapeWorkspaceHtml(markdownImageAlt || "Attached image")}" src="${escapeWorkspaceHtml(safeMarkdownImageUrl.href)}" /></a>`;
    } else if (backgroundText !== undefined) {
      html += `<span style="background-color:${getSafeWorkspaceTextColor(backgroundColor)}">${renderWorkspaceInlineHtml(backgroundText)}</span>`;
    } else if (coloredText !== undefined) {
      html += `<span style="color:${getSafeWorkspaceTextColor(textColor)}">${renderWorkspaceInlineHtml(coloredText)}</span>`;
    } else if (underlineText !== undefined) {
      html += `<u>${renderWorkspaceInlineHtml(underlineText)}</u>`;
    } else if (strikeText !== undefined) {
      html += `<s>${renderWorkspaceInlineHtml(strikeText)}</s>`;
    } else if (markdownLinkText && safeMarkdownUrl) {
      html += `<a href="${escapeWorkspaceHtml(safeMarkdownUrl.href)}" rel="noreferrer noopener" target="_blank">${renderWorkspaceInlineHtml(markdownLinkText)}</a>`;
    } else if (boldText) {
      html += `<strong>${renderWorkspaceInlineHtml(boldText)}</strong>`;
    } else if (codeText) {
      html += `<code>${escapeWorkspaceHtml(codeText)}</code>`;
    } else if (italicText) {
      html += `<em>${renderWorkspaceInlineHtml(italicText)}</em>`;
    } else if (safeRawUrl) {
      html += `<a href="${escapeWorkspaceHtml(safeRawUrl.href)}" rel="noreferrer noopener" target="_blank">${escapeWorkspaceHtml(safeRawUrl.text)}</a>${escapeWorkspaceHtml(safeRawUrl.suffix)}`;
    } else {
      html += escapeWorkspaceHtml(rawToken);
    }

    lastIndex = matchIndex + rawToken.length;
    match = inlinePattern.exec(text);
  }

  return `${html}${escapeWorkspaceHtml(text.slice(lastIndex))}`;
};

const workspaceMarkdownToEditorHtml = (value = "", { allowAttachments = false } = {}) => {
  const normalizedValue = `${value || ""}`.replace(/\r\n?/g, "\n");
  const lines = normalizedValue.split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    blocks.push(`<p>${renderWorkspaceInlineHtml(paragraphLines.join("\n"))}</p>`);
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(renderWorkspaceMarkdownListHtml(listItems));
    listItems = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const alignmentMatch = line.match(/^<div style="text-align:(center|right|justify)">([\s\S]*)<\/div>$/);
    if (alignmentMatch) {
      flushParagraph();
      flushList();
      blocks.push(`<p style="text-align:${alignmentMatch[1]}">${renderWorkspaceInlineHtml(alignmentMatch[2])}</p>`);
      continue;
    }
    const fenceMatch = line.match(/^```\s*([A-Za-z0-9_+-]+)?\s*$/);
    if (fenceMatch) {
      flushParagraph();
      flushList();
      const codeLines = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }
      blocks.push(getWorkspaceEditorCodeBlockHtml(codeLines.join("\n"), { allowAttachments }));
      continue;
    }

    const listEntry = getWorkspaceMarkdownListEntry(line);
    if (listEntry) {
      flushParagraph();
      listItems.push(listEntry);
      continue;
    }

    flushList();
    if (!line.trim()) {
      flushParagraph();
      blocks.push("<p><br /></p>");
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      const tag = headingMatch[1].length === 1 ? "h3" : "h4";
      blocks.push(`<${tag}>${renderWorkspaceInlineHtml(headingMatch[2])}</${tag}>`);
      continue;
    }

    const quoteMatch = line.match(/^>\s+(.+)$/);
    if (quoteMatch) {
      flushParagraph();
      blocks.push(`<blockquote>${renderWorkspaceInlineHtml(quoteMatch[1])}</blockquote>`);
      continue;
    }

    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  return blocks.join("") || "<p><br /></p>";
};

const getNodeInlineMarkdown = (node) => {
  if (!node) return "";
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const element = node;
  const tag = element.tagName.toLowerCase();
  if (tag === "br") return "\n";
  if (tag === "ul" || tag === "ol") return "";
  if (tag === "img") {
    const src = element.getAttribute("src") || "";
    if (!getSafeWorkspaceUrl(src)) return "";
    return `![${escapeWorkspaceMarkdownInline(element.getAttribute("alt") || "image")}](${src})`;
  }

  const childText = Array.from(element.childNodes).map(getNodeInlineMarkdown).join("");
  if (!childText) return "";
  if (tag === "font") {
    const color = getWorkspaceStyleColor(element.getAttribute("color") || element.style.color);
    return color ? `<span style="color:${color}">${childText}</span>` : childText;
  }
  if (tag === "strong" || tag === "b") return `**${childText}**`;
  if (tag === "em" || tag === "i") return `*${childText}*`;
  if (tag === "u") return `<u>${childText}</u>`;
  if (tag === "s" || tag === "strike" || tag === "del") return `~~${childText}~~`;
  if (tag === "code" && element.parentElement?.tagName?.toLowerCase() !== "pre") return `\`${childText.replace(/`/g, "'")}\``;
  if (tag === "a") {
    const href = element.getAttribute("href") || "";
    const safeUrl = getSafeWorkspaceUrl(href);
    return safeUrl ? `[${escapeWorkspaceMarkdownLinkLabel(childText)}](${safeUrl.href})` : childText;
  }
  if (tag === "span") {
    const backgroundColor = getWorkspaceStyleColor(element.style.backgroundColor);
    const color = getWorkspaceStyleColor(element.style.color);
    if (backgroundColor) return `<span style="background-color:${backgroundColor}">${childText}</span>`;
    if (color) return `<span style="color:${color}">${childText}</span>`;
  }
  return childText;
};

const getListItemOwnMarkdown = (item) => Array.from(item.childNodes)
  .filter((child) => !(child.nodeType === Node.ELEMENT_NODE && ["ul", "ol"].includes(child.tagName.toLowerCase())))
  .map(getNodeInlineMarkdown)
  .join("")
  .replace(/\n+/g, " ")
  .trim();

const getListMarkdown = (list, depth = 0) => {
  const ordered = list.tagName.toLowerCase() === "ol";
  const indent = "  ".repeat(depth);
  return Array.from(list.children)
    .filter((child) => child.tagName?.toLowerCase() === "li")
    .map((item, index) => {
      const marker = ordered ? `${index + 1}. ` : "- ";
      const ownText = getListItemOwnMarkdown(item);
      const nestedLists = Array.from(item.children)
        .filter((child) => ["ul", "ol"].includes(child.tagName.toLowerCase()))
        .map((child) => getListMarkdown(child, depth + 1))
        .filter(Boolean)
        .join("\n");
      return `${indent}${marker}${ownText || " "}${nestedLists ? `\n${nestedLists}` : ""}`;
    })
    .join("\n");
};

const workspaceEditorHtmlToMarkdown = (root) => {
  if (!root) return "";
  const lines = [];
  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (text.trim()) lines.push(text);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node;
    const tag = element.tagName.toLowerCase();
    if (tag === "br") {
      lines.push("");
    } else if (tag === "ul" || tag === "ol") {
      lines.push(getListMarkdown(element));
    } else if (tag === "blockquote") {
      const quote = getNodeInlineMarkdown(element).split("\n").map((line) => `> ${line}`).join("\n");
      lines.push(withWorkspaceTextAlignment(quote, element));
    } else if (tag === "pre") {
      const code = element.querySelector("code")?.innerText || element.innerText;
      lines.push(`\`\`\`\n${code.replace(/\n$/, "")}\n\`\`\``);
    } else if (tag === "h3") {
      lines.push(withWorkspaceTextAlignment(`# ${getNodeInlineMarkdown(element).trim()}`, element));
    } else if (tag === "h4") {
      lines.push(withWorkspaceTextAlignment(`## ${getNodeInlineMarkdown(element).trim()}`, element));
    } else {
      lines.push(withWorkspaceTextAlignment(getNodeInlineMarkdown(element).trim(), element));
    }
  });
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

const getEditableTextOffset = (root, range) => {
  if (!root || !range) return 0;
  let offset = 0;
  let found = false;

  const walk = (node) => {
    if (!node || found) return;
    if (node === range.startContainer) {
      offset += range.startOffset;
      found = true;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      offset += node.textContent?.length || 0;
      return;
    }
    Array.from(node.childNodes || []).forEach(walk);
  };

  walk(root);
  return offset;
};

const getEditableSelectionOffset = (root) => {
  if (typeof window === "undefined" || !root) return 0;
  const selection = window.getSelection();
  if (!selection?.rangeCount) return 0;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return root.innerText.length;
  return getEditableTextOffset(root, range);
};

const setEditableSelectionRange = (root, start = 0, end = start) => {
  if (typeof window === "undefined" || !root) return;
  const normalizedStart = Math.max(0, start);
  const normalizedEnd = Math.max(normalizedStart, end);
  const range = document.createRange();
  const selection = window.getSelection();
  let currentOffset = 0;
  let startSet = false;
  let endSet = false;

  const walk = (node) => {
    if (!node || endSet) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.textContent?.length || 0;
      const nextOffset = currentOffset + length;
      if (!startSet && normalizedStart <= nextOffset) {
        range.setStart(node, Math.max(0, Math.min(length, normalizedStart - currentOffset)));
        startSet = true;
      }
      if (!endSet && normalizedEnd <= nextOffset) {
        range.setEnd(node, Math.max(0, Math.min(length, normalizedEnd - currentOffset)));
        endSet = true;
      }
      currentOffset = nextOffset;
      return;
    }
    Array.from(node.childNodes || []).forEach(walk);
  };

  walk(root);
  if (!startSet) range.setStart(root, root.childNodes.length);
  if (!endSet) range.setEnd(root, root.childNodes.length);
  selection.removeAllRanges();
  selection.addRange(range);
};

const restoreEditableSelection = (root, savedRange) => {
  if (typeof window === "undefined" || !root || !savedRange) return false;
  const selection = window.getSelection();
  if (!selection) return false;
  try {
    if (root.contains(savedRange.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
      return true;
    }
  } catch {
    return false;
  }
  return false;
};


const renderPlainTextSegment = (text = "", key = "text") => (
  <React.Fragment key={key}>{text}</React.Fragment>
);

const appendRenderedSegment = (nodes, rendered) => {
  if (Array.isArray(rendered)) {
    nodes.push(...rendered.filter((node) => node !== null && node !== undefined && node !== ""));
  } else if (rendered !== null && rendered !== undefined && rendered !== "") {
    nodes.push(rendered);
  }
};

export const renderWorkspaceInlineText = (body = "", renderTextSegment = renderPlainTextSegment, keyPrefix = "workspace-inline") => {
  const text = `${body || ""}`;
  if (!text) return null;

  const nodes = [];
  const inlinePattern = new RegExp(String.raw`!\[([^\]\n]*)\]\((${workspaceUrlRegexSource})\)|<span\s+style=["']background-color:\s*(${workspaceColorPatternSource})["']>([\s\S]*?)<\/span>|<span\s+style=["']color:\s*(${workspaceColorPatternSource})["']>([\s\S]*?)<\/span>|<u>([\s\S]*?)<\/u>|~~([^~\n]+)~~|\[([^\]\n]+)\]\((${workspaceUrlRegexSource})\)|\*\*([^*\n]+)\*\*|` + "`([^`\\n]+)`" + String.raw`|\*([^*\n]+)\*|(${workspaceUrlRegexSource})`, "gi");
  const appendText = (segment, key) => {
    if (!segment) return;
    appendRenderedSegment(nodes, renderTextSegment(segment, key));
  };

  let lastIndex = 0;
  let match = inlinePattern.exec(text);

  while (match) {
    const matchIndex = match.index;
    appendText(text.slice(lastIndex, matchIndex), `${keyPrefix}-text-${matchIndex}-${nodes.length}`);

    const [rawToken, markdownImageAlt, markdownImageUrl, backgroundColor, backgroundText, textColor, coloredText, underlineText, strikeText, markdownLinkText, markdownLinkUrl, boldText, codeText, italicText, rawUrl] = match;
    const safeMarkdownImageUrl = markdownImageUrl ? getSafeWorkspaceUrl(markdownImageUrl) : null;
    const safeMarkdownUrl = markdownLinkUrl ? getSafeWorkspaceUrl(markdownLinkUrl) : null;
    const safeRawUrl = rawUrl ? getSafeWorkspaceUrl(rawUrl) : null;

    if (markdownImageUrl && safeMarkdownImageUrl) {
      nodes.push(
        <a className="workspace-rich-image-link" href={safeMarkdownImageUrl.href} key={`${keyPrefix}-image-${matchIndex}`} rel="noreferrer noopener" target="_blank">
          <img alt={markdownImageAlt || "Attached image"} src={safeMarkdownImageUrl.href} />
        </a>
      );
    } else if (backgroundText !== undefined) {
      nodes.push(
        <span className="workspace-rich-highlighted-text" key={`${keyPrefix}-background-${matchIndex}`} style={{ backgroundColor: getSafeWorkspaceTextColor(backgroundColor) }}>
          {renderWorkspaceInlineText(backgroundText, renderTextSegment, `${keyPrefix}-background-text-${matchIndex}`)}
        </span>
      );
    } else if (coloredText !== undefined) {
      nodes.push(
        <span className="workspace-rich-colored-text" key={`${keyPrefix}-color-${matchIndex}`} style={{ color: getSafeWorkspaceTextColor(textColor) }}>
          {renderWorkspaceInlineText(coloredText, renderTextSegment, `${keyPrefix}-color-text-${matchIndex}`)}
        </span>
      );
    } else if (underlineText !== undefined) {
      nodes.push(
        <u key={`${keyPrefix}-underline-${matchIndex}`}>
          {renderWorkspaceInlineText(underlineText, renderTextSegment, `${keyPrefix}-underline-text-${matchIndex}`)}
        </u>
      );
    } else if (strikeText !== undefined) {
      nodes.push(
        <s key={`${keyPrefix}-strike-${matchIndex}`}>
          {renderWorkspaceInlineText(strikeText, renderTextSegment, `${keyPrefix}-strike-text-${matchIndex}`)}
        </s>
      );
    } else if (markdownLinkText && safeMarkdownUrl) {
      nodes.push(
        <a className="workspace-rich-link" href={safeMarkdownUrl.href} key={`${keyPrefix}-link-${matchIndex}`} rel="noreferrer noopener" target="_blank">
          {renderWorkspaceInlineText(markdownLinkText, renderTextSegment, `${keyPrefix}-link-label-${matchIndex}`)}
        </a>
      );
    } else if (boldText) {
      nodes.push(
        <strong key={`${keyPrefix}-bold-${matchIndex}`}>
          {renderWorkspaceInlineText(boldText, renderTextSegment, `${keyPrefix}-bold-text-${matchIndex}`)}
        </strong>
      );
    } else if (codeText) {
      nodes.push(<code key={`${keyPrefix}-code-${matchIndex}`}>{codeText}</code>);
    } else if (italicText) {
      nodes.push(
        <em key={`${keyPrefix}-italic-${matchIndex}`}>
          {renderWorkspaceInlineText(italicText, renderTextSegment, `${keyPrefix}-italic-text-${matchIndex}`)}
        </em>
      );
    } else if (safeRawUrl) {
      nodes.push(
        <a className="workspace-rich-link" href={safeRawUrl.href} key={`${keyPrefix}-url-${matchIndex}`} rel="noreferrer noopener" target="_blank">
          {safeRawUrl.text}
        </a>
      );
      appendText(safeRawUrl.suffix, `${keyPrefix}-url-suffix-${matchIndex}`);
    } else {
      appendText(rawToken, `${keyPrefix}-raw-${matchIndex}`);
    }

    lastIndex = matchIndex + rawToken.length;
    match = inlinePattern.exec(text);
  }

  appendText(text.slice(lastIndex), `${keyPrefix}-tail-${nodes.length}`);
  return nodes;
};

function WorkspaceCodeBlock({ code = "" }) {
  const [copied, setCopied] = useState(false);
  const codeValue = `${code || ""}`;

  const copyCode = async (event) => {
    event?.stopPropagation?.();
    if (!codeValue || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(codeValue);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  return (
    <figure className="workspace-rich-code-card">
      <span className="workspace-rich-code-actions">
        <button
          aria-label={copied ? "Code copied" : "Copy code"}
          className="workspace-rich-code-copy"
          disabled={!codeValue}
          onClick={copyCode}
          onDoubleClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          title={copied ? "Copied" : "Copy code"}
          type="button"
        >
          {copied ? <CheckOutlined fontSize="inherit" /> : <ContentCopyOutlined fontSize="inherit" />}
        </button>
      </span>
      <pre className="workspace-rich-code-block">
        <code>{codeValue || " "}</code>
      </pre>
    </figure>
  );
}

export function WorkspaceRichText({ className = "", emptyText = "", renderTextSegment = renderPlainTextSegment, value = "" }) {
  const normalizedValue = `${value || ""}`.replace(/\r\n?/g, "\n");
  const text = normalizedValue.trim();

  if (!text) {
    return emptyText ? <span className={`workspace-rich-text is-empty ${className}`.trim()}>{emptyText}</span> : null;
  }

  const lines = normalizedValue.split("\n");
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    let cursor = 0;
    const listKey = `list-${blocks.length}`;
    const renderList = (depth, type, keyPrefix) => {
      const ListTag = type === "ordered" ? "ol" : "ul";
      const children = [];
      while (cursor < listItems.length) {
        const entry = listItems[cursor];
        if (entry.depth < depth || (entry.depth === depth && entry.type !== type)) break;
        if (entry.depth > depth) break;
        cursor += 1;
        const nestedLists = [];
        while (cursor < listItems.length && listItems[cursor].depth > depth) {
          nestedLists.push(renderList(listItems[cursor].depth, listItems[cursor].type, `${keyPrefix}-nested-${nestedLists.length}`));
        }
        children.push(
          <li key={`${keyPrefix}-item-${children.length}`}>
            {renderWorkspaceInlineText(entry.content, renderTextSegment, `${keyPrefix}-item-${children.length}`)}
            {nestedLists}
          </li>
        );
      }
      return <ListTag className="workspace-rich-list" key={keyPrefix}>{children}</ListTag>;
    };
    while (cursor < listItems.length) blocks.push(renderList(listItems[cursor].depth, listItems[cursor].type, `${listKey}-${cursor}`));
    listItems = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const alignmentMatch = line.match(/^<div style="text-align:(center|right|justify)">([\s\S]*)<\/div>$/);
    if (alignmentMatch) {
      flushList();
      blocks.push(<p key={`aligned-${index}`} style={{ textAlign: alignmentMatch[1] }}>{renderWorkspaceInlineText(alignmentMatch[2], renderTextSegment, `aligned-${index}`)}</p>);
      continue;
    }
    const fenceMatch = line.match(/^```\s*([A-Za-z0-9_+-]+)?\s*$/);
    if (fenceMatch) {
      flushList();
      const codeLines = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }
      blocks.push(
        <WorkspaceCodeBlock code={codeLines.join("\n")} key={`code-block-${index}`} language={fenceMatch[1] || ""} />
      );
      continue;
    }

    const listEntry = getWorkspaceMarkdownListEntry(line);
    if (listEntry) {
      listItems.push(listEntry);
      continue;
    }

    flushList();

    if (!line.trim()) {
      blocks.push(<span aria-hidden="true" className="workspace-rich-break" key={`break-${index}`} />);
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const HeadingTag = headingMatch[1].length === 1 ? "h3" : "h4";
      blocks.push(
        <HeadingTag key={`heading-${index}`}>
          {renderWorkspaceInlineText(headingMatch[2], renderTextSegment, `heading-${index}`)}
        </HeadingTag>
      );
      continue;
    }

    const quoteMatch = line.match(/^>\s+(.+)$/);
    if (quoteMatch) {
      blocks.push(
        <blockquote key={`quote-${index}`}>
          {renderWorkspaceInlineText(quoteMatch[1], renderTextSegment, `quote-${index}`)}
        </blockquote>
      );
      continue;
    }

    blocks.push(
      <p key={`paragraph-${index}`}>
        {renderWorkspaceInlineText(line, renderTextSegment, `paragraph-${index}`)}
      </p>
    );
  }

  flushList();

  return <div className={`workspace-rich-text ${className}`.trim()}>{blocks}</div>;
}

export function WorkspaceEditor({
  actions = null,
  ariaLabel = "",
  attachmentAccept = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip",
  attachmentDisabled = false,
  autoFocus = false,
  className = "",
  compact = false,
  disabled = false,
  helperText = "",
  id = "",
  inputClassName = "",
  inputRef = null,
  label = "",
  maxLength,
  mentionOptions = [],
  minRows,
  name = "",
  onAttachmentChange,
  onBlur,
  onChange,
  onEscape,
  onFocus,
  onKeyDown,
  onSubmitShortcut,
  placeholder = "",
  required = false,
  rows = 4,
  toolbar = true,
  value = "",
}) {
  const textareaRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const toolbarShellRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const linkPopoverRef = useRef(null);
  const colorPopoverRef = useRef(null);
  const backgroundColorPopoverRef = useRef(null);
  const linkTextInputRef = useRef(null);
  const linkUrlInputRef = useRef(null);
  const savedEditableRangeRef = useRef(null);
  const lastEmittedValueRef = useRef(`${value || ""}`);
  const editorHydratedRef = useRef(false);
  const savedSelectionRef = useRef({ displayEnd: 0, displayStart: 0, sourceEnd: 0, sourceStart: 0 });
  const generatedId = useRef(`workspace-editor-${Math.random().toString(36).slice(2, 10)}`);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [linkDraft, setLinkDraft] = useState({ text: "", url: "" });
  const [linkError, setLinkError] = useState("");
  const [linkEditingExisting, setLinkEditingExisting] = useState(false);
  const [linkPopoverPosition, setLinkPopoverPosition] = useState({ left: 10, top: 10 });
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [colorDraft, setColorDraft] = useState(workspaceEditorTextColors[0]);
  const [colorPopoverPosition, setColorPopoverPosition] = useState({ left: 10, top: 10 });
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [backgroundColorDraft, setBackgroundColorDraft] = useState(workspaceEditorBackgroundColors[0]);
  const [backgroundColorPopoverPosition, setBackgroundColorPopoverPosition] = useState({ left: 10, top: 10 });
  const [backgroundColorPopoverOpen, setBackgroundColorPopoverOpen] = useState(false);
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});
  const [editorIsEmpty, setEditorIsEmpty] = useState(!`${value || ""}`.trim());
  const editorId = id || generatedId.current;
  const textValue = `${value || ""}`;
  const editorValue = textValue;
  const attachmentEnabled = Boolean(onAttachmentChange);
  const normalizedMentions = useMemo(
    () => mentionOptions.map(normalizeMentionOption).filter((option) => option.handle),
    [mentionOptions]
  );
  const visibleMentions = mentionQuery
    ? normalizedMentions
      .filter((option) => option.handle.toLowerCase().includes(mentionQuery.text) || option.label.toLowerCase().includes(mentionQuery.text))
      .slice(0, 6)
    : [];

  const setTextareaRef = useCallback((node) => {
    textareaRef.current = node;
    if (node) {
      node.setSelectionRange = (start = 0, end = start) => setEditableSelectionRange(node, start, end);
    }
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) inputRef.current = node;
  }, [inputRef]);

  const setTextareaSelection = useCallback((start, end = start) => {
    window.requestAnimationFrame(() => {
      const editor = textareaRef.current;
      if (!editor) return;
      editor.focus();
      setEditableSelectionRange(editor, start, end);
    });
  }, []);

  const commitChange = (nextValue, selectionStart, selectionEnd = selectionStart) => {
    const editor = textareaRef.current;
    if (editor) {
      editor.innerHTML = workspaceMarkdownToEditorHtml(nextValue, { allowAttachments: attachmentEnabled });
      editor.value = nextValue;
      lastEmittedValueRef.current = nextValue;
      editorHydratedRef.current = true;
      setEditorIsEmpty(!nextValue.trim());
    }
    onChange?.(nextValue);
    if (Number.isFinite(selectionStart)) setTextareaSelection(selectionStart, selectionEnd);
  };

  const saveCurrentEditableRange = () => {
    if (typeof window === "undefined") return;
    const editor = textareaRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    savedEditableRangeRef.current = range.cloneRange();
    const offset = getEditableSelectionOffset(editor);
    savedSelectionRef.current = {
      displayEnd: offset,
      displayStart: offset,
      sourceEnd: offset,
      sourceStart: offset,
    };
    const anchorNode = selection.anchorNode;
    const anchorElement = anchorNode?.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode?.parentElement;
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strike: document.queryCommandState("strikeThrough"),
      unorderedList: document.queryCommandState("insertUnorderedList"),
      orderedList: document.queryCommandState("insertOrderedList"),
      alignLeft: document.queryCommandState("justifyLeft"),
      alignCenter: document.queryCommandState("justifyCenter"),
      alignRight: document.queryCommandState("justifyRight"),
      alignJustify: document.queryCommandState("justifyFull"),
      quote: Boolean(anchorElement?.closest?.("blockquote")),
      codeBlock: Boolean(anchorElement?.closest?.("pre")),
    });
  };

  const syncEditorChange = () => {
    const editor = textareaRef.current;
    if (!editor) return;
    const nextValue = workspaceEditorHtmlToMarkdown(editor);
    editor.value = nextValue;
    lastEmittedValueRef.current = nextValue;
    editorHydratedRef.current = true;
    setEditorIsEmpty(!nextValue.trim());
    onChange?.(nextValue);
    updateMentionQuery(editor);
  };

  const runEditorCommand = (command, valueForCommand = null) => {
    if (disabled || typeof document === "undefined") return;
    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    restoreEditableSelection(editor, savedEditableRangeRef.current);
    document.execCommand(command, false, valueForCommand);
    saveCurrentEditableRange();
    syncEditorChange();
  };

  const handleListContinuationEnter = (event) => {
    if (event.key !== "Tab" || event.metaKey || event.ctrlKey || event.altKey) return false;
    const selection = window.getSelection();
    const anchorElement = selection?.anchorNode?.nodeType === Node.ELEMENT_NODE ? selection.anchorNode : selection?.anchorNode?.parentElement;
    if (!anchorElement?.closest?.("li")) return false;
    event.preventDefault();
    runEditorCommand(event.shiftKey ? "outdent" : "indent");
    return true;
  };

  const getTextareaCursorPosition = (textarea, cursor, preferredWidth = 320, anchorOffset = 0) => {
    if (typeof window === "undefined" || typeof document === "undefined") return { left: 10, top: textarea.offsetTop + textarea.offsetHeight + 8 };
    const field = textarea.parentElement;
    if (!field) return { left: 10, top: textarea.offsetTop + textarea.offsetHeight + 8 };

    const textareaStyles = window.getComputedStyle(textarea);
    const mirror = document.createElement("div");
    const marker = document.createElement("span");
    const fieldRect = field.getBoundingClientRect();
    const lineHeight = Number.parseFloat(textareaStyles.lineHeight) || 22;

    mirror.style.position = "absolute";
    mirror.style.visibility = "hidden";
    mirror.style.pointerEvents = "none";
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordBreak = "break-word";
    mirror.style.overflowWrap = "break-word";
    mirror.style.boxSizing = textareaStyles.boxSizing;
    mirror.style.width = `${textarea.clientWidth}px`;
    mirror.style.padding = textareaStyles.padding;
    mirror.style.border = textareaStyles.border;
    mirror.style.font = textareaStyles.font;
    mirror.style.letterSpacing = textareaStyles.letterSpacing;
    mirror.style.lineHeight = textareaStyles.lineHeight;
    mirror.style.top = `${textarea.getBoundingClientRect().top + window.scrollY}px`;
    mirror.style.left = `${textarea.getBoundingClientRect().left + window.scrollX}px`;
    mirror.textContent = `${textarea.value || textarea.innerText || ""}`.slice(0, cursor) || "\u200b";
    marker.textContent = "\u200b";
    mirror.appendChild(marker);
    document.body.appendChild(mirror);

    const markerRect = marker.getBoundingClientRect();
    document.body.removeChild(mirror);

    const rawLeft = markerRect.left - fieldRect.left - textarea.scrollLeft;
    const rawTop = markerRect.top - fieldRect.top - textarea.scrollTop + lineHeight + 8;
    const menuWidth = Math.min(preferredWidth, Math.max(220, field.clientWidth - 20));

    return {
      left: Math.max(10, Math.min(rawLeft - anchorOffset, field.clientWidth - menuWidth - 10)),
      top: Math.max(textarea.offsetTop + 8, rawTop),
    };
  };

  const getMentionMenuPosition = (textarea, cursor) => getTextareaCursorPosition(textarea, cursor, 320);

  const getCurrentEditableRange = () => {
    if (typeof window === "undefined") return null;
    const editor = textareaRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    return editor.contains(range.commonAncestorContainer) ? range : null;
  };

  const getEditablePopoverPosition = (preferredWidth = 320, anchorOffset = 0) => {
    const editor = textareaRef.current;
    const field = editor?.parentElement;
    const range = getCurrentEditableRange() || savedEditableRangeRef.current;
    if (!editor || !field) return { left: 10, top: 10 };
    const fieldRect = field.getBoundingClientRect();
    const rangeRect = range?.getBoundingClientRect?.();
    const editorRect = editor.getBoundingClientRect();
    const menuWidth = Math.min(preferredWidth, Math.max(220, field.clientWidth - 20));
    const rawLeft = (rangeRect?.left || editorRect.left) - fieldRect.left - anchorOffset;
    const rawTop = (rangeRect?.bottom || editorRect.top + 26) - fieldRect.top + 8;
    return {
      left: Math.max(10, Math.min(rawLeft, field.clientWidth - menuWidth - 10)),
      top: Math.max(editor.offsetTop + 8, rawTop),
    };
  };

  const getAnchorFromRange = (range) => {
    if (!range) return null;
    const startElement = range.startContainer.nodeType === Node.ELEMENT_NODE ? range.startContainer : range.startContainer.parentElement;
    const endElement = range.endContainer.nodeType === Node.ELEMENT_NODE ? range.endContainer : range.endContainer.parentElement;
    return startElement?.closest?.("a") || endElement?.closest?.("a") || null;
  };

  const updateMentionQuery = (textarea) => {
    if (!normalizedMentions.length) {
      setMentionQuery(null);
      return;
    }
    const cursor = getEditableSelectionOffset(textarea);
    const beforeCursor = `${textarea.value || textarea.innerText || ""}`.slice(0, cursor);
    const match = beforeCursor.match(/(^|\s)@([A-Za-z0-9._-]*)$/);
    setMentionQuery(match ? { text: match[2].toLowerCase(), cursor, position: getMentionMenuPosition(textarea, cursor) } : null);
  };

  const selectMention = (option) => {
    const textarea = textareaRef.current;
    if (!textarea || !mentionQuery) return;
    const currentText = workspaceEditorHtmlToMarkdown(textarea);
    const beforeCursor = currentText.slice(0, mentionQuery.cursor);
    const mentionStart = beforeCursor.lastIndexOf("@");
    if (mentionStart < 0) return;
    const sourceStart = mentionStart;
    const sourceEnd = mentionQuery.cursor;
    const nextValue = `${currentText.slice(0, sourceStart)}${option.handle} ${currentText.slice(sourceEnd)}`;
    const nextCursor = mentionStart + option.handle.length + 1;
    setMentionQuery(null);
    commitChange(nextValue, nextCursor);
  };

  const insertCodeBlock = () => {
    if (disabled || typeof document === "undefined") return;
    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    restoreEditableSelection(editor, savedEditableRangeRef.current);
    const selectedText = window.getSelection()?.toString() || "code";
    document.execCommand("insertHTML", false, `${getWorkspaceEditorCodeBlockHtml(selectedText, { allowAttachments: attachmentEnabled })}<p><br></p>`);
    syncEditorChange();
  };

  const continueBelowCodeBlock = (targetCodeBlock = null) => {
    if (disabled || typeof document === "undefined") return;
    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    restoreEditableSelection(editor, savedEditableRangeRef.current);
    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode;
    const anchorElement = anchorNode?.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode?.parentElement;
    const codeBlock = targetCodeBlock || anchorElement?.closest?.("pre");
    if (!codeBlock || !editor.contains(codeBlock)) return;
    const paragraph = document.createElement("p");
    paragraph.appendChild(document.createElement("br"));
    codeBlock.insertAdjacentElement("afterend", paragraph);
    const range = document.createRange();
    range.setStart(paragraph, 0);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    saveCurrentEditableRange();
    syncEditorChange();
  };

  const copyCodeBlock = async (codeBlock) => {
    const code = codeBlock?.querySelector("code")?.innerText || "";
    if (!code || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Clipboard access can be denied by the browser; keep the code block unchanged.
    }
  };

  const deleteCodeBlock = (codeBlock) => {
    const editor = textareaRef.current;
    if (!editor || !codeBlock || !editor.contains(codeBlock)) return;
    const nextSibling = codeBlock.nextElementSibling;
    codeBlock.remove();
    const paragraph = nextSibling?.tagName?.toLowerCase() === "p" ? nextSibling : document.createElement("p");
    if (!paragraph.parentElement) {
      paragraph.appendChild(document.createElement("br"));
      editor.appendChild(paragraph);
    }
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(paragraph, 0);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    saveCurrentEditableRange();
    syncEditorChange();
  };

  const handleCodeBlockAction = (event) => {
    const actionButton = event.target?.closest?.("[data-workspace-code-action]");
    const editor = textareaRef.current;
    if (!actionButton || !editor?.contains(actionButton)) return false;
    event.preventDefault();
    event.stopPropagation();
    const codeBlock = actionButton.closest("pre");
    if (actionButton.dataset.workspaceCodeAction === "copy") copyCodeBlock(codeBlock);
    if (actionButton.dataset.workspaceCodeAction === "attachment") attachmentInputRef.current?.click();
    if (actionButton.dataset.workspaceCodeAction === "delete") deleteCodeBlock(codeBlock);
    if (actionButton.dataset.workspaceCodeAction === "continue") continueBelowCodeBlock(codeBlock);
    return true;
  };

  const applyTextColor = (color = colorDraft) => {
    const safeColor = getSafeWorkspaceTextColor(color);
    setColorDraft(safeColor);
    setColorPopoverOpen(false);
    runEditorCommand("foreColor", safeColor);
  };

  const applyBackgroundColor = (color = backgroundColorDraft) => {
    const safeColor = getSafeWorkspaceTextColor(color);
    setBackgroundColorDraft(safeColor);
    setBackgroundColorPopoverOpen(false);
    runEditorCommand("hiliteColor", safeColor);
  };

  const openColorPopover = () => {
    if (disabled) return;
    if (colorPopoverOpen) {
      setColorPopoverOpen(false);
      return;
    }
    saveCurrentEditableRange();
    const cursor = getEditableSelectionOffset(textareaRef.current);
    const selectionEnd = cursor;
    savedSelectionRef.current = {
      displayEnd: selectionEnd,
      displayStart: cursor,
      sourceEnd: selectionEnd,
      sourceStart: cursor,
    };
    setColorPopoverPosition(getEditablePopoverPosition(260, 24));
    setMentionQuery(null);
    setLinkPopoverOpen(false);
    setBackgroundColorPopoverOpen(false);
    setColorPopoverOpen(true);
  };

  const openBackgroundColorPopover = () => {
    if (disabled) return;
    if (backgroundColorPopoverOpen) {
      setBackgroundColorPopoverOpen(false);
      return;
    }
    saveCurrentEditableRange();
    const cursor = getEditableSelectionOffset(textareaRef.current);
    const selectionEnd = cursor;
    savedSelectionRef.current = {
      displayEnd: selectionEnd,
      displayStart: cursor,
      sourceEnd: selectionEnd,
      sourceStart: cursor,
    };
    setBackgroundColorPopoverPosition(getEditablePopoverPosition(260, 24));
    setMentionQuery(null);
    setLinkPopoverOpen(false);
    setColorPopoverOpen(false);
    setBackgroundColorPopoverOpen(true);
  };

  const openLinkPopover = ({ focusUrl = false, range = null, toggle = true } = {}) => {
    if (disabled) return;
    if (toggle && linkPopoverOpen) {
      closeLinkPopover();
      return;
    }
    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    if (range) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    const activeRange = getCurrentEditableRange();
    if (!activeRange) return;
    savedEditableRangeRef.current = activeRange.cloneRange();
    const anchor = getAnchorFromRange(activeRange);
    const selectedDisplayText = (anchor?.textContent || activeRange.toString() || "").trim();
    const safeSelectedUrl = getSafeWorkspaceUrl(selectedDisplayText);
    const selectionOffset = getEditableSelectionOffset(editor);
    savedSelectionRef.current = {
      displayEnd: selectionOffset,
      displayStart: selectionOffset,
      sourceEnd: selectionOffset,
      sourceStart: selectionOffset,
    };
    setLinkPopoverPosition(getEditablePopoverPosition(360, 24));
    setMentionQuery(null);
    setColorPopoverOpen(false);
    setBackgroundColorPopoverOpen(false);
    setLinkError("");
    setLinkEditingExisting(Boolean(anchor));
    setLinkDraft({
      text: selectedDisplayText,
      url: anchor?.getAttribute("href") || safeSelectedUrl?.href || "",
    });
    setLinkPopoverOpen(true);
    window.requestAnimationFrame(() => {
      const input = focusUrl || (selectedDisplayText && !safeSelectedUrl) ? linkUrlInputRef.current : linkTextInputRef.current;
      input?.focus();
      input?.select?.();
    });
  };

  const closeLinkPopover = useCallback(({ restoreFocus = true } = {}) => {
    setLinkPopoverOpen(false);
    setLinkError("");
    setLinkEditingExisting(false);
    if (restoreFocus) {
      const editor = textareaRef.current;
      if (editor) {
        editor.focus();
        restoreEditableSelection(editor, savedEditableRangeRef.current);
      }
    }
  }, []);

  const openExistingLinkPopoverFromClick = (event) => {
    const anchor = event.target?.closest?.("a");
    if (!anchor || disabled) return false;
    event.preventDefault();
    const range = document.createRange();
    range.selectNodeContents(anchor);
    openLinkPopover({ focusUrl: true, range, toggle: false });
    return true;
  };

  const applyLinkDraft = (event) => {
    event?.preventDefault?.();
    const safeUrl = getSafeWorkspaceUrl(linkDraft.url.trim());
    if (!safeUrl) {
      setLinkError("Enter a valid http or https URL.");
      linkUrlInputRef.current?.focus();
      return;
    }

    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    restoreEditableSelection(editor, savedEditableRangeRef.current);
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const displayText = linkDraft.text.trim() || range?.toString()?.trim() || safeUrl.text;
    if (range) {
      range.deleteContents();
      const anchor = document.createElement("a");
      anchor.href = safeUrl.href;
      anchor.rel = "noreferrer noopener";
      anchor.target = "_blank";
      anchor.textContent = displayText;
      range.insertNode(anchor);
      range.setStartAfter(anchor);
      range.setEndAfter(anchor);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    setLinkPopoverOpen(false);
    setLinkError("");
    setLinkEditingExisting(false);
    syncEditorChange();
  };

  const removeLinkDraft = () => {
    const editor = textareaRef.current;
    if (!editor) return;
    editor.focus();
    restoreEditableSelection(editor, savedEditableRangeRef.current);
    document.execCommand("unlink", false);
    setLinkPopoverOpen(false);
    setLinkError("");
    setLinkEditingExisting(false);
    syncEditorChange();
  };

  const toolbarActions = [
    { activeKey: "bold", icon: <FormatBoldOutlined fontSize="inherit" />, title: "Bold", action: () => runEditorCommand("bold") },
    { activeKey: "italic", icon: <FormatItalicOutlined fontSize="inherit" />, title: "Italic", action: () => runEditorCommand("italic") },
    { activeKey: "underline", icon: <FormatUnderlinedOutlined fontSize="inherit" />, title: "Underline", action: () => runEditorCommand("underline") },
    { activeKey: "strike", icon: <StrikethroughSOutlined fontSize="inherit" />, title: "Strikethrough", action: () => runEditorCommand("strikeThrough") },
    { icon: <FormatColorTextOutlined fontSize="inherit" />, title: "Text color", action: openColorPopover },
    { icon: <FormatColorFillOutlined fontSize="inherit" />, title: "Background color", action: openBackgroundColorPopover },
    { activeKey: "unorderedList", icon: <FormatListBulletedOutlined fontSize="inherit" />, title: "Bullet list", action: () => runEditorCommand("insertUnorderedList") },
    { activeKey: "orderedList", icon: <FormatListNumberedOutlined fontSize="inherit" />, title: "Numbered list", action: () => runEditorCommand("insertOrderedList") },
    { type: "layout-menu", title: "Alignment and indentation" },
    { activeKey: "quote", icon: <FormatQuoteOutlined fontSize="inherit" />, title: "Quote", action: () => runEditorCommand("formatBlock", "blockquote") },
    { activeKey: "codeBlock", icon: <CodeOutlined fontSize="inherit" />, title: "Code block", action: insertCodeBlock },
    { icon: <LinkOutlined fontSize="inherit" />, title: "Insert link", action: () => openLinkPopover() },
    ...(onAttachmentChange ? [{ icon: <AttachFileOutlined fontSize="inherit" />, title: "Attach image or file" }] : []),
  ];

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const minimumRows = minRows || rows;
    const lineHeight = Number.parseFloat(window.getComputedStyle(textarea).lineHeight) || 20;
    const minHeight = Math.max(minimumRows * lineHeight + 22, 44);
    textarea.style.minHeight = `${minHeight}px`;
  }, [editorValue, minRows, rows]);

  useEffect(() => {
    const editor = textareaRef.current;
    if (!editor) return;
    editor.value = textValue;
    if (document.activeElement === editor && lastEmittedValueRef.current === textValue) return;
    editor.innerHTML = workspaceMarkdownToEditorHtml(textValue, { allowAttachments: attachmentEnabled });
    setEditorIsEmpty(!textValue.trim());
  }, [attachmentEnabled, textValue]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      const cursorPosition = textarea.innerText.length;
      setEditableSelectionRange(textarea, cursorPosition, cursorPosition);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [autoFocus]);

  useEffect(() => {
    if (!linkPopoverOpen) return undefined;

    const handlePointerDown = (event) => {
      if (linkPopoverRef.current?.contains(event.target)) return;
      if (toolbarShellRef.current?.querySelector('[aria-label="Insert link"]')?.contains(event.target)) return;
      closeLinkPopover({ restoreFocus: false });
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [closeLinkPopover, linkPopoverOpen]);

  useEffect(() => {
    if (!colorPopoverOpen) return undefined;

    const handlePointerDown = (event) => {
      if (colorPopoverRef.current?.contains(event.target)) return;
      if (toolbarShellRef.current?.querySelector('[aria-label="Text color"]')?.contains(event.target)) return;
      setColorPopoverOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [colorPopoverOpen]);

  useEffect(() => {
    if (!backgroundColorPopoverOpen) return undefined;

    const handlePointerDown = (event) => {
      if (backgroundColorPopoverRef.current?.contains(event.target)) return;
      if (toolbarShellRef.current?.querySelector('[aria-label="Background color"]')?.contains(event.target)) return;
      setBackgroundColorPopoverOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [backgroundColorPopoverOpen]);

  useEffect(() => {
    if (!layoutMenuOpen) return undefined;
    const closeLayoutMenu = (event) => {
      if (!layoutMenuRef.current?.contains(event.target)) setLayoutMenuOpen(false);
    };
    document.addEventListener("mousedown", closeLayoutMenu);
    document.addEventListener("touchstart", closeLayoutMenu);
    return () => {
      document.removeEventListener("mousedown", closeLayoutMenu);
      document.removeEventListener("touchstart", closeLayoutMenu);
    };
  }, [layoutMenuOpen]);

  return (
    <div className={`workspace-editor ${compact ? "is-compact" : ""} ${disabled ? "is-disabled" : ""} ${className}`.trim()}>
      {label ? <label className="workspace-editor-label" htmlFor={editorId}>{label}{required ? <span aria-hidden="true"> *</span> : null}</label> : null}
      <div className="workspace-editor-toolbar-shell" ref={toolbarShellRef}>
        {toolbar ? (
          <div aria-label="Editor formatting" className="workspace-editor-toolbar" role="toolbar">
            {toolbarActions.map((item) => (
              item.type === "layout-menu" ? (
                <div className="workspace-editor-layout-menu" key={item.title} ref={layoutMenuRef}>
                  <button
                    aria-expanded={layoutMenuOpen}
                    aria-haspopup="menu"
                  aria-label={item.title}
                  className={layoutMenuOpen ? "is-active" : ""}
                  disabled={disabled}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setLayoutMenuOpen((open) => !open);
                    }}
                    title={item.title}
                    type="button"
                  >
                    <FormatAlignLeftOutlined fontSize="inherit" />
                    <ArrowDropDownOutlined className="workspace-editor-layout-menu-chevron" fontSize="inherit" />
                  </button>
                  {layoutMenuOpen ? (
                    <div aria-label="Alignment and indentation" className="workspace-editor-layout-menu-popover" onMouseDown={(event) => event.stopPropagation()} role="menu">
                      <button onClick={() => { runEditorCommand("justifyLeft"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatAlignLeftOutlined fontSize="inherit" /><span>Align left</span></button>
                      <button onClick={() => { runEditorCommand("justifyCenter"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatAlignCenterOutlined fontSize="inherit" /><span>Align center</span></button>
                      <button onClick={() => { runEditorCommand("justifyRight"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatAlignRightOutlined fontSize="inherit" /><span>Align right</span></button>
                      <button onClick={() => { runEditorCommand("justifyFull"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatAlignJustifyOutlined fontSize="inherit" /><span>Justify</span></button>
                      <span aria-hidden="true" className="workspace-editor-layout-menu-divider" />
                      <button onClick={() => { runEditorCommand("indent"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatIndentIncreaseOutlined fontSize="inherit" /><span>Increase indent</span></button>
                      <button onClick={() => { runEditorCommand("outdent"); setLayoutMenuOpen(false); }} role="menuitem" type="button"><FormatIndentDecreaseOutlined fontSize="inherit" /><span>Decrease indent</span></button>
                    </div>
                  ) : null}
                </div>
              ) : item.title === "Attach image or file" ? (
                <label aria-label={item.title} className="workspace-editor-attachment-button" key={item.title} title={item.title}>
                  {item.icon}
                  <input accept={attachmentAccept} disabled={disabled || attachmentDisabled} multiple onChange={onAttachmentChange} ref={attachmentInputRef} type="file" />
                </label>
              ) : <button
                aria-label={item.title}
                aria-pressed={item.title === "Insert link" ? linkPopoverOpen : item.title === "Text color" ? colorPopoverOpen : item.title === "Background color" ? backgroundColorPopoverOpen : item.activeKey ? Boolean(activeFormats[item.activeKey]) : undefined}
                className={(item.title === "Insert link" && linkPopoverOpen) || (item.title === "Text color" && colorPopoverOpen) || (item.title === "Background color" && backgroundColorPopoverOpen) || (item.activeKey && activeFormats[item.activeKey]) ? "is-active" : ""}
                disabled={disabled}
                key={item.title}
                onClick={item.action}
                title={item.title}
                type="button"
              >
                {item.icon}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="workspace-editor-input-shell">
        <div
          aria-label={label ? undefined : ariaLabel || placeholder || "Editor"}
          aria-multiline="true"
          className={`workspace-editor-surface ${inputClassName} ${editorIsEmpty ? "is-empty" : ""}`.trim()}
          contentEditable={!disabled}
          data-placeholder={placeholder}
          id={editorId}
          onBeforeInput={(event) => {
            if (!maxLength) return;
            const currentValue = workspaceEditorHtmlToMarkdown(event.currentTarget);
            if (currentValue.length >= maxLength && event.inputType?.startsWith("insert")) event.preventDefault();
          }}
          onBlur={(event) => {
            saveCurrentEditableRange();
            onBlur?.(event);
            window.setTimeout(() => {
              setMentionQuery(null);
            }, 120);
          }}
          onInput={syncEditorChange}
          onClick={(event) => {
            if (handleCodeBlockAction(event)) return;
            saveCurrentEditableRange();
            updateMentionQuery(event.currentTarget);
            openExistingLinkPopoverFromClick(event);
          }}
          onFocus={onFocus}
          onKeyDown={(event) => {
            if (handleListContinuationEnter(event)) return;
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              const selection = window.getSelection();
              const anchorNode = selection?.anchorNode;
              const anchorElement = anchorNode?.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode?.parentElement;
              if (anchorElement?.closest?.("pre")) {
                event.preventDefault();
                continueBelowCodeBlock();
                return;
              }
            }
            if ((event.metaKey || event.ctrlKey) && event.key === "b") {
              event.preventDefault();
              runEditorCommand("bold");
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === "i") {
              event.preventDefault();
              runEditorCommand("italic");
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === "u") {
              event.preventDefault();
              runEditorCommand("underline");
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "x") {
              event.preventDefault();
              runEditorCommand("strikeThrough");
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
              event.preventDefault();
              openLinkPopover();
              return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && onSubmitShortcut) {
              event.preventDefault();
              onSubmitShortcut(event);
              return;
            }
            if (event.key === "Escape" && onEscape) {
              event.preventDefault();
              onEscape(event);
              return;
            }
            onKeyDown?.(event);
          }}
          onKeyUp={(event) => {
            saveCurrentEditableRange();
            updateMentionQuery(event.currentTarget);
          }}
          onMouseUp={saveCurrentEditableRange}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData?.getData("text/plain") || "";
            document.execCommand("insertText", false, text);
            syncEditorChange();
          }}
          ref={setTextareaRef}
          role="textbox"
          suppressContentEditableWarning
          tabIndex={disabled ? -1 : 0}
        />
        <textarea
          aria-hidden="true"
          className="workspace-editor-hidden-field"
          maxLength={maxLength}
          name={name}
          readOnly
          required={required}
          tabIndex={-1}
          value={textValue}
        />
        {visibleMentions.length ? (
          <div className="workspace-editor-mention-menu" role="listbox" style={{ left: mentionQuery?.position?.left ?? 10, top: mentionQuery?.position?.top ?? 10 }}>
            {visibleMentions.map((option) => (
              <button aria-selected={false} key={option.handle} onMouseDown={(event) => { event.preventDefault(); selectMention(option); }} role="option" type="button">
                <WorkspaceUserAvatar className="workspace-editor-mention-avatar" user={option.profile || { name: option.label }} />
                <span>
                  <strong>{option.label}</strong>
                  {option.subtitle ? <small>{option.subtitle}</small> : null}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {linkPopoverOpen ? (
          <form
            aria-label="Create link"
            className="workspace-editor-link-popover"
            ref={linkPopoverRef}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                closeLinkPopover();
              }
            }}
            onSubmit={applyLinkDraft}
            style={{ left: linkPopoverPosition.left, top: linkPopoverPosition.top }}
          >
            <header>
              <strong>{linkEditingExisting ? "Edit link" : "Add link"}</strong>
              <button aria-label="Close link editor" onClick={() => closeLinkPopover()} type="button">×</button>
            </header>
            <div>
              <label>
                Display text
                <input
                  onChange={(event) => setLinkDraft((current) => ({ ...current, text: event.target.value }))}
                  placeholder="Text shown to users"
                  ref={linkTextInputRef}
                  value={linkDraft.text}
                />
              </label>
              <label>
                URL
                <input
                  inputMode="url"
                  onChange={(event) => {
                    setLinkDraft((current) => ({ ...current, url: event.target.value }));
                    if (linkError) setLinkError("");
                  }}
                  placeholder="https://example.com"
                  ref={linkUrlInputRef}
                  required
                  value={linkDraft.url}
                />
              </label>
            </div>
            {linkError ? <p role="alert">{linkError}</p> : null}
            <footer>
              {linkEditingExisting ? <button className="workspace-editor-link-remove" onClick={removeLinkDraft} type="button">Remove link</button> : null}
              <button onClick={() => closeLinkPopover()} type="button">Cancel</button>
              <button type="submit">Apply</button>
            </footer>
          </form>
        ) : null}
        {colorPopoverOpen ? (
          <div
            aria-label="Choose text color"
            className="workspace-editor-color-popover"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setColorPopoverOpen(false);
                setTextareaSelection(savedSelectionRef.current.displayStart, savedSelectionRef.current.displayEnd);
              }
            }}
            ref={colorPopoverRef}
            role="dialog"
            style={{ left: colorPopoverPosition.left, top: colorPopoverPosition.top }}
          >
            <div className="workspace-editor-color-grid">
              {workspaceEditorTextColors.map((color) => (
                <button
                  aria-label={`Apply text color ${color}`}
                  className={colorDraft.toLowerCase() === color.toLowerCase() ? "is-active" : ""}
                  key={color}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyTextColor(color)}
                  style={{ "--workspace-editor-swatch": color }}
                  type="button"
                />
              ))}
            </div>
            <label>
              Custom
              <input
                aria-label="Custom text color"
                onChange={(event) => setColorDraft(getSafeWorkspaceTextColor(event.target.value))}
                type="color"
                value={colorDraft}
              />
            </label>
            <button onClick={() => applyTextColor(colorDraft)} type="button">Apply color</button>
          </div>
        ) : null}
        {backgroundColorPopoverOpen ? (
          <div
            aria-label="Choose background color"
            className="workspace-editor-color-popover"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setBackgroundColorPopoverOpen(false);
                setTextareaSelection(savedSelectionRef.current.displayStart, savedSelectionRef.current.displayEnd);
              }
            }}
            ref={backgroundColorPopoverRef}
            role="dialog"
            style={{ left: backgroundColorPopoverPosition.left, top: backgroundColorPopoverPosition.top }}
          >
            <div className="workspace-editor-color-grid">
              {workspaceEditorBackgroundColors.map((color) => (
                <button
                  aria-label={`Apply background color ${color}`}
                  className={backgroundColorDraft.toLowerCase() === color.toLowerCase() ? "is-active" : ""}
                  key={color}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyBackgroundColor(color)}
                  style={{ "--workspace-editor-swatch": color }}
                  type="button"
                />
              ))}
            </div>
            <label>
              Custom
              <input
                aria-label="Custom background color"
                onChange={(event) => setBackgroundColorDraft(getSafeWorkspaceTextColor(event.target.value))}
                type="color"
                value={backgroundColorDraft}
              />
            </label>
            <button onClick={() => applyBackgroundColor(backgroundColorDraft)} type="button">Apply background</button>
          </div>
        ) : null}
      </div>
      {helperText || maxLength || actions ? (
        <div className="workspace-editor-footer">
          <small>{helperText || (maxLength ? `${textValue.length}/${maxLength}` : "")}</small>
          {actions ? <div>{actions}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
